import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IUserProfile } from '@shared/interfaces/user/user-profile';
import {
  BehaviorSubject,
  finalize,
  firstValueFrom,
  forkJoin,
  mergeMap,
  Observable,
  ReplaySubject,
  Subscriber,
  take,
  throwError,
} from 'rxjs';
import { ApiService } from '@shared/services/api.service';
import { SignalRService } from '@shared/services/signal-r.service';
import { StorageService } from '@shared/services/storage.service';
import { ZSuiteService } from '@shared/services/zsuite.service';
import { ZSuiteHelper } from '@shared/helpers/zsuite-helper';
import { UserPublisherService } from '@shared/services/publishers/user-publisher.service';
import {
  adminCacheBuster$,
  statsAndResourcesCacheBuster$,
} from '@shared/constants/cache-busters';
import { environment } from '@env';
import { GlobalLoaderService } from '@shared/services/global-loader.service';
import { map, catchError, tap, switchMap, throwIfEmpty } from 'rxjs/operators';
import { IUserAuthorization } from '@shared/interfaces/user/user-authorization';
import { AccessService } from 'src/app/modules/access/access.service';
import { PERMISSIONS } from '@access/permissions';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  user$: ReplaySubject<IUserProfile | null> =
    new ReplaySubject<IUserProfile | null>(1);
  user: IUserProfile | null = null;
  groupingEnabled$!: BehaviorSubject<boolean>;

  private loginSubscription = new BehaviorSubject<boolean>(false);
  private currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<any>;

  constructor(
    private readonly storage: StorageService,
    private readonly router: Router,
    private readonly api: ApiService,
    private readonly zSuiteService: ZSuiteService,
    private readonly signalRService: SignalRService,
    private readonly translateService: TranslateService,
    private userPublisherService: UserPublisherService,
    private loaderService: GlobalLoaderService,
    private readonly accessService: AccessService
  ) {
    this.user$.subscribe((user) => (this.user = user));

    this.userPublisherService
      .getRefreshUserDataObservable()
      .pipe(mergeMap((_) => this.api.getUserProfile()))
      .subscribe((res) => {
        this.user$.next(res);
      });

    this.currentUserSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem('userAuth') || '{}')
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue() {
    return this.currentUserSubject.value;
  }

  logout() {
    this.api
      .logout()
      .pipe(
        finalize(() => {
          this.logoutLocally();
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      )
      .subscribe();
  }

  logoutLocally() {
    localStorage.removeItem('userAuth');
    this.currentUserSubject.next(null);

    this.loaderService.show();

    this.storage.clearStorage();
    this.storage.clearSessionStorage();
    statsAndResourcesCacheBuster$.next();
    adminCacheBuster$.next();
    this.accessService.clear();
    this.user$.next(null);
    this.loginSubscription.next(false);

    ZSuiteHelper.isZSuite() && this.zSuiteService.logout();
    this.signalRService.stopConnection();
    
    this.router
      .navigateByUrl('/')
      .then(() => this.loaderService.hide())
      .catch((err) => err);
  }

  login(email: string, password: string) {
    return new Observable<Boolean | any>((subscriber) => {
      this.api.login({ email: email, password: password }).subscribe({
        next: (res) => {
          if ('requires2FA' in res) {
            subscriber.next(res);
          } else {
            this.setLogin(res, subscriber);
          }
        },
        error: (res) => {
          this.loginSubscription.next(false);
          subscriber.error(res);
        },
      });
    });
  }

  login2Fa(twoFactorToken: string, code: string) {
    return new Observable<Boolean | any>((subscriber) => {
      this.api
        .loginTwoFactor({ twoFactorToken: twoFactorToken, code: code })
        .subscribe({
          next: (res: IUserAuthorization) => {
            this.setLogin(res, subscriber);
          },
          error: (res) => {
            this.loginSubscription.next(false);
            subscriber.error(res);
          },
        });
    });
  }

  setLogin(userAuth: IUserAuthorization, subscriber: Subscriber<any>) {
    this.loaderService.show();
    userAuth && userAuth.accessToken && userAuth.refreshToken;
    localStorage.setItem('userAuth', JSON.stringify(userAuth));
    this.currentUserSubject.next(userAuth);

    this.signalRService.userAuthorization = () => this.currentUserValue;
    this.signalRService.startConnection();

    //get user data
    this.updateUserData().then(() => {
      subscriber.next({ requires2FA: false });
      this.loaderService.hide();
    });
  }

  async updateUserData(): Promise<boolean> {
    try {
      const result = await firstValueFrom(
        forkJoin({
          userProfile: this.api.getUserProfile(),
          access: this.accessService.reload(),
        })
      );

      this.user = result.userProfile ?? null;

      if (result.userProfile) {
        this.user$.next(result.userProfile);
      }

      this.loginSubscription.next(true);
      this.createGrouping();

      return true;
    } catch (error) {
      return false;
    }
  }

  register(email: string, password: string) {
    return this.api.register({ email: email, password: password });
  }

  recoverPassword(email: string) {
    return this.api.forgetPassword({ email: email });
  }

  resendRegistrationCode(email: string) {
    return this.api.resendConfirmationEmail({ email: email });
  }

  resetPassword(email: string, password: string, recoveryCode: string) {
    return this.api.resetPassword({
      email: email,
      newPassword: password,
      resetCode: recoveryCode,
    });
  }

  private createGrouping() {
    let grouping = JSON.parse(this.storage.getStorage(this.storage.grouping));

    if (grouping === undefined || grouping === null || grouping) {
      grouping = this.accessService.hasAll([
        PERMISSIONS.DeviceGroups.View,
        PERMISSIONS.DeviceGroups.Manage,
      ])
        ? true
        : false;
      this.storage.setStorage(JSON.stringify(grouping), this.storage.grouping);
    }

    this.groupingEnabled$ = new BehaviorSubject<boolean>(grouping);
  }

  isLoggedInSync(): boolean {
    return this.loginSubscription.getValue();
  }

  isLoggedIn(): Observable<boolean> {
    return this.loginSubscription.asObservable();
  }

  checkAuthAndRedirectToId(
    cookieValue: { package: number; billingPeriod: string } | null = null
  ) {
    this.isLoggedIn()
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.router.navigateByUrl('/dashboard');
          return;
        }
        this.setCookie(cookieValue);
        window.open(environment.redirectTo.url, '_self');
      });
  }

  setCookie(
    cookieValue: { package: number; billingPeriod: string } | null = null
  ) {
    let data = {
      name: environment.appName,
      url: `${environment.baseUrl}dashboard`,
      issuer: 'incloud',
    };
    const expiry = new Date();
    expiry.setTime(expiry.getTime() + 30 * 60 * 1000);

    data = Object.assign(data, cookieValue);
    document.cookie = `${environment.redirectTo.cookieKey}=${JSON.stringify(
      data
    )};path=/;domain=${
      environment.redirectTo.domain
    };expires=${expiry.toUTCString()}`;
  }

  setPaymentsCookie() {
    const data = {
      label: this.translateService.instant('common.back-to', {
        value: environment.appName,
      }),
      url: `${this.api.url}dashboard`,
    };

    const expiry = new Date();
    expiry.setTime(expiry.getTime() + 30 * 60 * 1000);

    document.cookie = `${
      environment.payments.redirectCookieKey
    }=${JSON.stringify(data)};path=/;domain=${
      environment.payments.domain
    };expires=${expiry.toUTCString()}`;
  }

  refreshAccess(): Observable<Object> {
    if (!this.currentUserValue) {
      return throwError(() => new Error('No current user'));
    }

    return this.api
      .refreshToken({ refreshToken: this.currentUserValue.refreshToken })
      .pipe(
        map((user) => {
          if (user && user.accessToken) {
            const currentUser = this.currentUserValue;
            currentUser.accessToken = user.accessToken;
            localStorage.setItem('userAuth', JSON.stringify(currentUser));
            this.currentUserSubject.next(currentUser);
          }
          return user;
        }),
        catchError((error) => {
          this.logout();
          throw error;
        })
      );
  }

  isAdmin() {
    if (!this.user) return false;
    return this.accessService.hasPermission(PERMISSIONS.Admin.Dashboard.View);
  }

  isOwner(ownerId: string): boolean {
    return this.user?.organizationId === ownerId;
  }

  setGrouping(groupingEnabled: boolean) {
    if (
      this.accessService.hasAll([
        PERMISSIONS.DeviceGroups.View,
        PERMISSIONS.DeviceGroups.Manage,
      ])
    ) {
      this.storage.setStorage(
        JSON.stringify(groupingEnabled),
        this.storage.grouping
      );
      this.groupingEnabled$.next(groupingEnabled);
    } else {
      this.groupingEnabled$.next(false);
    }
  }
}
